import { message, result, createDataItemSigner } from '@permaweb/aoconnect';

// AO process ID for EVM Address Book
const AO_PROCESS_ID = '1D3aZ70eRDrTUbD-wTiwc12iG4jC2Jk-j1A5m98LV3g';

// Create signer function for ArConnect
const createArConnectSigner = () => {
  return createDataItemSigner(window.arweaveWallet);
};

export const aoUtils = {
  // Link an EVM address to the current Arweave address
  async linkEvmAddress(evmAddress: string): Promise<{ success: boolean; error?: string }> {
    try {
      const messageId = await message({
        process: AO_PROCESS_ID,
        tags: [
          { name: 'Action', value: 'Link' },
          { name: 'EvmAddress', value: evmAddress }
        ],
        signer: createArConnectSigner()
      });

      const response = await result({
        message: messageId,
        process: AO_PROCESS_ID
      });

      console.log('Link response:', response);

      // Check if linking was successful
      let linkingResult = null;
      
      if (response.Messages && response.Messages.length > 0) {
        // Find the message with linking response
        linkingResult = response.Messages.find((msg: any) => {
          if (Array.isArray(msg.Tags)) {
            const actionTag = msg.Tags.find((tag: any) => tag.name === 'Action');
            return actionTag && (actionTag.value === 'Linking-Success' || actionTag.value === 'Linking-Error');
          }
          return false;
        });
      }

      if (linkingResult) {
        const actionTag = linkingResult.Tags.find((tag: any) => tag.name === 'Action');
        if (actionTag?.value === 'Linking-Success') {
          return { success: true };
        } else if (actionTag?.value === 'Linking-Error') {
          const errorTag = linkingResult.Tags.find((tag: any) => tag.name === 'Error');
          return { success: false, error: errorTag?.value || 'Linking failed' };
        }
      }

      // For debugging - return more info about what we got
      console.log('Unexpected response structure:', JSON.stringify(response, null, 2));
      return { success: false, error: `Unknown response from process. Got: ${JSON.stringify(response.Messages?.[0]?.Tags || 'no messages')}` };
    } catch (error) {
      console.error('Error linking EVM address:', error);
      return { success: false, error: 'Failed to communicate with AO process' };
    }
  },

  // Get the linked EVM address for the current user
  async getLinkedAddress(): Promise<{ address?: string; error?: string }> {
    try {
      const messageId = await message({
        process: AO_PROCESS_ID,
        tags: [
          { name: 'Action', value: 'GetAddress' }
        ],
        signer: createArConnectSigner()
      });

      const response = await result({
        message: messageId,
        process: AO_PROCESS_ID
      });

      // Check the response
      let addressResult = null;
      
      if (response.Messages && response.Messages.length > 0) {
        addressResult = response.Messages.find((msg: any) => {
          if (Array.isArray(msg.Tags)) {
            const actionTag = msg.Tags.find((tag: any) => tag.name === 'Action');
            return actionTag && (actionTag.value === 'Address-Response' || actionTag.value === 'Address-Error');
          }
          return false;
        });
      }

      if (addressResult) {
        const actionTag = addressResult.Tags.find((tag: any) => tag.name === 'Action');
        if (actionTag?.value === 'Address-Response') {
          const evmAddressTag = addressResult.Tags.find((tag: any) => tag.name === 'EvmAddress');
          return { address: evmAddressTag?.value };
        } else if (actionTag?.value === 'Address-Error') {
          const errorTag = addressResult.Tags.find((tag: any) => tag.name === 'Error');
          return { error: errorTag?.value };
        }
      }

      return { error: 'Unknown response from process' };
    } catch (error) {
      console.error('Error getting linked address:', error);
      return { error: 'Failed to communicate with AO process' };
    }
  },

  // Get process info
  async getProcessInfo(): Promise<{ name?: string; usersCount?: number; error?: string }> {
    try {
      const messageId = await message({
        process: AO_PROCESS_ID,
        tags: [
          { name: 'Action', value: 'Info' }
        ],
        signer: createArConnectSigner()
      });

      const response = await result({
        message: messageId,
        process: AO_PROCESS_ID
      });

      // The info should be in the response tags
      if (response.Messages && response.Messages.length > 0) {
        const infoMsg = response.Messages[0];
        return {
          name: infoMsg.Tags?.Name,
          usersCount: parseInt(infoMsg.Tags?.UsersCount || '0')
        };
      }

      return { error: 'No info received from process' };
    } catch (error) {
      console.error('Error getting process info:', error);
      return { error: 'Failed to get process info' };
    }
  }
};

// Utility function to validate EVM address format
export const isValidEvmAddress = (address: string): boolean => {
  return /^0x[0-9a-fA-F]{40}$/.test(address);
};
